var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPhoneNumber, PhoneAuthProvider, RecaptchaVerifier, signOut, signInWithCredential, sendPasswordResetEmail as firebaseSendPasswordResetEmail } from 'firebase/auth';
import { getApps, initializeApp } from 'firebase/app';
const AuthContext = createContext(undefined);
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
// Initialize Firebase if it hasn't been initialized yet
const ensureFirebaseInit = () => {
    if (getApps().length === 0) {
        // Initialize Firebase with values from environment variables
        const firebaseConfig = {
            apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
            authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
            projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
            storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
            messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
            appId: process.env.REACT_APP_FIREBASE_APP_ID || ""
        };
        initializeApp(firebaseConfig);
    }
};
export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // Initialize Firebase before accessing auth
    ensureFirebaseInit();
    const auth = getAuth();
    // Set up auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });
        // Clean up subscription
        return unsubscribe;
    }, [auth]);
    // Email/Password authentication
    const signInWithEmail = (email, password) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield signInWithEmailAndPassword(auth, email, password);
            // Add a small delay to ensure Firebase auth state has fully propagated
            yield new Promise(resolve => setTimeout(resolve, 1000));
        }
        finally {
            // Loading will be set to false by the onAuthStateChanged listener
        }
    });
    const signUpWithEmail = (email, password) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield createUserWithEmailAndPassword(auth, email, password);
            // Add a small delay to ensure Firebase auth state has fully propagated
            yield new Promise(resolve => setTimeout(resolve, 1000));
        }
        finally {
            // Loading will be set to false by the onAuthStateChanged listener
        }
    });
    // Phone authentication
    const startPhoneSignIn = (phoneNumber) => __awaiter(void 0, void 0, void 0, function* () {
        const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
        });
        try {
            const confirmationResult = yield signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
            return confirmationResult.verificationId;
        }
        finally {
            // Clean up the recaptcha verifier to prevent memory leaks
            recaptchaVerifier.clear();
        }
    });
    const confirmPhoneSignIn = (verificationId, code) => __awaiter(void 0, void 0, void 0, function* () {
        const credential = PhoneAuthProvider.credential(verificationId, code);
        yield signInWithCredential(auth, credential);
    });
    const logout = () => {
        return signOut(auth);
    };
    const sendPasswordResetEmail = (email) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            yield firebaseSendPasswordResetEmail(auth, email);
        }
        finally {
            setLoading(false);
        }
    });
    const value = {
        currentUser,
        loading,
        signInWithEmail,
        signUpWithEmail,
        startPhoneSignIn,
        confirmPhoneSignIn,
        logout,
        sendPasswordResetEmail
    };
    return (_jsx(AuthContext.Provider, { value: value, children: children }));
};
